import LaunchOutlined from "@mui/icons-material/LaunchOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { gql } from "generated-graphql";
import {
  LeadAgenciesPickerQuery,
  LeadAgencyPickerFragment,
} from "generated-graphql/graphql";
import { forwardRef } from "react";
import { ControllerFieldState } from "react-hook-form";
import EncampAutocomplete from "./Forms/EncampAutocomplete";

// Base props for the picker
type LeadAgencyPickerProps = {
  defaultSearch?: string;
  onChange?: (value: LeadAgencyPickerFragment | null) => void;
  value?: LeadAgencyPickerFragment | null;
  disabled?: boolean;
  helperText?: string | React.JSX.Element;
  displayAgencyLink?: boolean;
} & ControllerFieldState;

// GraphQL fragments and queries
const LEAD_AGENCY_PICKER_FRAGMENT = gql(`
  fragment LeadAgencyPicker on LeadAgency {
    id
    name
    address
    city
    state
    zip
    email
    attn
    consolidated
    phone
  }
`);

const LEAD_AGENCIES_SEARCH_QUERY = gql(`
  query LeadAgenciesPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!], $tenantId: String) {
    leadAgencies(search: $search, page: $page, pageSize: $pageSize, sort: $sort, tenantId: $tenantId) {
      items {
        ...LeadAgencyPicker
      }
      count
    }
  }
`);

// Function to format Lead Agency option labels
const getLeadAgencyOptionLabel = (leadAgency: LeadAgencyPickerFragment) => {
  return `${leadAgency.name} ${leadAgency.city}, ${leadAgency.state}`;
};

// Function to get items from query response
const getLeadAgencyItems = (data: LeadAgenciesPickerQuery) =>
  data.leadAgencies?.items ?? [];

// Main picker component
export const LeadAgencyPicker = forwardRef<
  HTMLDivElement,
  LeadAgencyPickerProps
>(function LeadAgencyPicker(
  {
    onChange,
    value,
    error,
    disabled,
    defaultSearch,
    helperText,
    displayAgencyLink,
  },
  ref
) {
  return (
    <EncampAutocomplete
      query={LEAD_AGENCIES_SEARCH_QUERY}
      variables={{
        search: defaultSearch,
      }}
      getItems={getLeadAgencyItems}
      getOptionLabel={getLeadAgencyOptionLabel}
      onChange={onChange}
      value={value || null}
      error={error}
      disabled={disabled}
      helperText={helperText}
      additionalEndAdornment={displayAgencyLink && agencyLinkAdornment(value)}
      label="Lead Agency"
    />
  );
});

function agencyLinkAdornment(
  value: LeadAgencyPickerFragment | null | undefined
) {
  return (
    value && (
      <Tooltip title="Open Lead Agency">
        <IconButton
          size="small"
          sx={{
            padding: "4px",
            color: "primary.main",
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the dropdown
            window.open(
              `/staff/agencies/njLeadAgencies?omnisearch=id:${value.id}`,
              "_blank"
            );
          }}
        >
          <LaunchOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  );
}
